
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';

const C = {
  deep:   '#0d0b14',
  card:   '#15121e',
  raised: '#1a1626',
  violet: '#5a3d99',
  accent: '#7c5cbf',
  lilac:  '#c9a0e8',
  ivory:  '#f2ece0',
  muted:  'rgba(242,236,224,0.6)',
  faint:  'rgba(242,236,224,0.06)',
  border: 'rgba(201,160,232,0.12)',
  gold:   '#e2b96a',
};
const font = {
  display: "'DM Serif Display', serif",
  body:    "'Playfair Display', serif",
  ui:      "'Inter', sans-serif",
};
const BG = 'linear-gradient(160deg, #0d0b14 0%, #110d1a 40%, #160e20 70%, #0d0b14 100%)';

const TIERS = [
  {
    id:       'whisper',
    priceId:  'price_1T8s3fIsytfhEBpLwKVtP86L',
    name:     'Whisper',
    price:    9.99,
    stories:  4,
    cadence:  'One new fantasy a week',
    features: ['4 stories per month', 'All moods & settings', 'All 9 narrator voices', 'Full personalisation'],
    highlight: false,
  },
  {
    id:       'desire',
    priceId:  'price_1T8s3fIsytfhEBpLpZ3tB8DN',
    name:     'Desire',
    price:    19.99,
    stories:  10,
    cadence:  '2–3 stories a week',
    features: ['10 stories per month', 'All moods & settings', 'All 9 narrator voices', 'Full personalisation', 'Priority generation'],
    highlight: true,
  },
  {
    id:       'obsession',
    priceId:  'price_1T8s3eIsytfhEBpL4DkGrXgJ',
    name:     'Obsession',
    price:    44.99,
    stories:  30,
    cadence:  'A new story every night',
    features: ['30 stories per month', 'All moods & settings', 'All 9 narrator voices', 'Full personalisation', 'Priority generation', 'Early access to new features'],
    highlight: false,
  },
];

export default function Subscribe() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [error, setError]     = useState('');

  const handleSubscribe = async (tier) => {
    setLoading(tier.id);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      // Guest checkout allowed — no auth required before payment
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/subscription/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ priceId: tier.priceId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start checkout');
      window.location.href = data.url;
    } catch (e) {
      setError(e.message);
      setLoading(null);
    }
  };

  return (
    <div style={{ minHeight:'100vh', background:BG, color:C.ivory,
      fontFamily:font.ui, fontWeight:300, padding:'0 0 60px',
      display:'flex', flexDirection:'column', alignItems:'center' }}>

      {/* Ambient glow */}
      <div style={{ position:'fixed', width:'500px', height:'500px', borderRadius:'50%',
        background:'radial-gradient(circle, rgba(90,61,153,0.14) 0%, transparent 70%)',
        top:'-100px', left:'50%', transform:'translateX(-50%)', pointerEvents:'none', zIndex:0 }} />

      {/* Header */}
      <div style={{ width:'100%', maxWidth:'480px', padding:'28px 24px 0',
        display:'flex', justifyContent:'space-between', alignItems:'center', position:'relative', zIndex:1 }}>
        <button onClick={() => navigate(-1)} style={{ background:'transparent', border:'none',
          color:C.muted, cursor:'pointer', fontSize:'13px', fontFamily:font.ui }}>← Back</button>
        <img src="/logo.png" alt="" style={{ width:'40px', filter:'drop-shadow(0 0 8px rgba(90,61,153,0.5))' }} />
      </div>

      {/* Hero text */}
      <div style={{ textAlign:'center', padding:'32px 28px 36px', maxWidth:'400px', position:'relative', zIndex:1 }}>
        <h1 style={{ fontFamily:font.display, fontStyle:'italic', fontSize:'32px',
          fontWeight:400, color:C.ivory, margin:'0 0 12px', lineHeight:1.2 }}>
          Choose your desire
        </h1>
        <p style={{ fontSize:'14px', color:C.muted, lineHeight:1.7, margin:0 }}>
          Every plan includes fully personalised stories, written and narrated just for you.
          Cancel anytime.
        </p>
      </div>

      {error && (
        <div style={{ width:'100%', maxWidth:'420px', padding:'0 24px', marginBottom:'16px', position:'relative', zIndex:1 }}>
          <div style={{ background:'rgba(200,50,50,0.15)', border:'1px solid rgba(200,50,50,0.3)',
            borderRadius:'10px', padding:'12px 16px', fontSize:'13px', color:'#f08080' }}>{error}</div>
        </div>
      )}

      {/* Tier cards */}
      <div style={{ width:'100%', maxWidth:'420px', padding:'0 24px',
        display:'flex', flexDirection:'column', gap:'14px', position:'relative', zIndex:1 }}>
        {TIERS.map(tier => (
          <div key={tier.id} style={{
            background: tier.highlight ? 'rgba(90,61,153,0.15)' : C.card,
            border: `1px solid ${tier.highlight ? C.accent : C.border}`,
            borderRadius:'18px', padding:'24px', paddingTop: tier.highlight ? '44px' : '24px',
            position:'relative', overflow:'hidden',
            boxShadow: tier.highlight ? '0 4px 32px rgba(90,61,153,0.25)' : 'none',
          }}>

            {tier.highlight && (
              <div style={{ position:'absolute', top:'14px', right:'14px',
                background:`linear-gradient(135deg, ${C.violet}, ${C.accent})`,
                borderRadius:'20px', padding:'4px 12px',
                fontSize:'10px', fontFamily:font.ui, fontWeight:300,
                color:C.ivory, letterSpacing:'0.1em' }}>
                MOST POPULAR
              </div>
            )}

            {/* Tier name + price */}
            <div style={{ marginBottom:'6px', display:'flex', alignItems:'baseline', justifyContent:'space-between' }}>
              <div style={{ fontFamily:font.display, fontStyle:'italic', fontSize:'26px', color:C.ivory }}>
                {tier.name}
              </div>
              <div style={{ textAlign:'right' }}>
                <span style={{ fontFamily:font.ui, fontSize:'24px', fontWeight:300, color:C.ivory }}>
                  ${tier.price}
                </span>
                <span style={{ fontSize:'12px', color:C.muted }}>/mo</span>
              </div>
            </div>

            {/* Cadence */}
            <div style={{ fontSize:'13px', color:C.lilac, marginBottom:'18px',
              fontFamily:font.body, fontStyle:'italic', opacity:0.85 }}>
              {tier.cadence}
            </div>

            {/* Stories count — big visual */}
            <div style={{ display:'flex', alignItems:'center', gap:'10px',
              background:'rgba(201,160,232,0.06)', border:`1px solid ${C.border}`,
              borderRadius:'12px', padding:'12px 16px', marginBottom:'16px' }}>
              <span style={{ fontFamily:font.display, fontStyle:'italic',
                fontSize:'32px', color:C.lilac, lineHeight:1 }}>{tier.stories}</span>
              <div>
                <div style={{ fontSize:'13px', color:C.ivory, marginBottom:'1px' }}>stories per month</div>
                <div style={{ fontSize:'11px', color:C.muted }}>
                  ${(tier.price / tier.stories).toFixed(2)} per story
                </div>
              </div>
            </div>

            {/* Features */}
            <div style={{ display:'flex', flexDirection:'column', gap:'8px', marginBottom:'20px' }}>
              {tier.features.map(f => (
                <div key={f} style={{ display:'flex', alignItems:'center', gap:'10px',
                  fontSize:'13px', color:C.muted }}>
                  <span style={{ color:C.lilac, fontSize:'10px', flexShrink:0 }}>✦</span>
                  {f}
                </div>
              ))}
            </div>

            {/* CTA */}
            <button
              onClick={() => handleSubscribe(tier)}
              disabled={!!loading}
              style={{
                width:'100%', padding:'15px',
                background: tier.highlight
                  ? `linear-gradient(135deg, ${C.violet} 0%, ${C.accent} 60%, #9b6fd4 100%)`
                  : 'rgba(201,160,232,0.08)',
                border: tier.highlight ? 'none' : `1px solid ${C.border}`,
                borderRadius:'12px', cursor: loading ? 'wait' : 'pointer',
                color: C.ivory,
                fontFamily:font.ui, fontWeight:300, fontSize:'14px',
                letterSpacing:'0.07em', transition:'all 0.2s',
                boxShadow: tier.highlight ? '0 4px 24px rgba(90,61,153,0.4)' : 'none',
              }}>
              {loading === tier.id ? 'Loading…' : `Start ${tier.name}`}
            </button>
          </div>
        ))}
      </div>

      {/* Footer note */}
      <div style={{ marginTop:'28px', fontSize:'12px', color:'rgba(242,236,224,0.3)',
        textAlign:'center', maxWidth:'320px', lineHeight:1.6, position:'relative', zIndex:1 }}>
        No commitment. Cancel anytime from your account page.
        Try a free sample story before you subscribe.
      </div>

    </div>
  );
}
